import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesKPIsAPI, ordenesSeedAPI, ordenesServicioAPI } from '../../lib/ordenesApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    ClipboardList,
    Users,
    Car,
    Package,
    DollarSign,
    Clock,
    CheckCircle,
    FileText,
    Settings,
    BarChart3,
    Plus,
    ArrowLeft,
    RefreshCw,
    AlertCircle,
    Wrench,
    TrendingUp,
    Loader2,
    Calendar,
    Award,
    Cpu,
} from 'lucide-react';

const ESTADO_CONFIG = {
    pendiente: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pendientes' },
    en_proceso: { color: 'bg-blue-100 text-blue-700', icon: Wrench, label: 'En Proceso' },
    realizada: { color: 'bg-purple-100 text-purple-700', icon: CheckCircle, label: 'Por Facturar' },
    facturada: { color: 'bg-green-100 text-green-700', icon: DollarSign, label: 'Facturadas' },
    cancelada: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Canceladas' },
};

export default function OrdenesDashboardPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    const [kpis, setKpis] = useState(null);
    const [ordenesRecientes, setOrdenesRecientes] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [kpisRes, ordenesRes] = await Promise.all([
                ordenesKPIsAPI.get(),
                ordenesServicioAPI.getAll({ limit: 5 }),
            ]);
            setKpis(kpisRes.data);
            setOrdenesRecientes(ordenesRes.data.slice(0, 5));
        } catch (err) {
            console.error('Error loading data:', err);
            if (err.response?.status === 404) {
                toast.info('No hay datos aún', {
                    description: 'Puedes crear datos de demostración para comenzar.'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSeedDemo = async () => {
        setSeeding(true);
        try {
            await ordenesSeedAPI.seedDemo();
            toast.success('Datos de demostración creados');
            loadData();
        } catch (err) {
            toast.error('Error al crear datos de demostración');
        } finally {
            setSeeding(false);
        }
    };

    if (permisosLoading || loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-28" />
                    ))}
                </div>
                <Skeleton className="h-64" />
            </div>
        );
    }

    const noData = !kpis || (kpis.total_clientes === 0 && kpis.total_ordenes === 0);

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/home')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <ClipboardList className="h-6 w-6" />
                            Órdenes de Servicio
                        </h1>
                        <p className="text-muted-foreground">Gestión de instalaciones y asistencias técnicas</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadData} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                    {tienePermiso('crear_ordenes') && (
                        <Button onClick={() => navigate('/ordenes/nueva')} data-testid="new-order-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Nueva Orden
                        </Button>
                    )}
                </div>
            </div>

            {/* Empty State */}
            {noData && (
                <Card className="border-dashed border-2">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                        <Package className="h-16 w-16 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold mb-2">Sin datos</h3>
                        <p className="text-muted-foreground mb-4 text-center">
                            No hay clientes ni órdenes registradas. Puedes crear datos de demostración para explorar el módulo.
                        </p>
                        <Button onClick={handleSeedDemo} disabled={seeding}>
                            {seeding ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Creando...
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Crear Datos Demo
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* KPIs */}
            {kpis && !noData && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ordenes/ordenes')}>
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Órdenes del Mes</p>
                                        <p className="text-3xl font-bold">{kpis.ordenes_mes}</p>
                                    </div>
                                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                        <ClipboardList className="h-6 w-6 text-blue-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ordenes/ordenes?estado=realizada')}>
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Por Facturar</p>
                                        <p className="text-3xl font-bold">{kpis.por_facturar}</p>
                                    </div>
                                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                                        <FileText className="h-6 w-6 text-purple-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ordenes/clientes')}>
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Clientes</p>
                                        <p className="text-3xl font-bold">{kpis.total_clientes}</p>
                                    </div>
                                    <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                        <Users className="h-6 w-6 text-green-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">Facturado (Mes)</p>
                                        <p className="text-2xl font-bold">${kpis.facturacion_mes?.toLocaleString('es-CL') || 0}</p>
                                    </div>
                                    <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                                        <TrendingUp className="h-6 w-6 text-emerald-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Stats por estado */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        {Object.entries(kpis.ordenes_por_estado || {}).filter(([estado]) => estado !== 'asignada').map(([estado, count]) => {
                            const config = ESTADO_CONFIG[estado] || { color: 'bg-gray-100 text-gray-700', icon: FileText, label: estado };
                            const Icon = config.icon;
                            return (
                                <Card 
                                    key={estado} 
                                    className="cursor-pointer hover:shadow-md transition-shadow"
                                    onClick={() => navigate(`/ordenes/ordenes?estado=${estado}`)}
                                >
                                    <CardContent className="pt-4 pb-4">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-full ${config.color}`}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <p className="text-xl font-bold">{count}</p>
                                                <p className="text-xs text-muted-foreground">{config.label}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Stats secundarias */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ordenes/vehiculos')}>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <Car className="h-8 w-8 text-muted-foreground" />
                                    <div>
                                        <p className="text-2xl font-bold">{kpis.total_vehiculos}</p>
                                        <p className="text-sm text-muted-foreground">Vehículos Registrados</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ordenes/dispositivos-instalados')}>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <Package className="h-8 w-8 text-muted-foreground" />
                                    <div>
                                        <p className="text-2xl font-bold">{kpis.total_dispositivos}</p>
                                        <p className="text-sm text-muted-foreground">Dispositivos Instalados</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ordenes/inventario')}>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <Package className="h-8 w-8 text-green-500" />
                                    <div>
                                        <p className="text-2xl font-bold">{kpis.inventario_disponible}</p>
                                        <p className="text-sm text-muted-foreground">Stock Disponible</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Órdenes recientes */}
                    {ordenesRecientes.length > 0 && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Clock className="h-5 w-5" />
                                    Órdenes Recientes
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {ordenesRecientes.map((orden) => {
                                        const config = ESTADO_CONFIG[orden.estado] || ESTADO_CONFIG.pendiente;
                                        return (
                                            <div 
                                                key={orden.id} 
                                                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                                                onClick={() => navigate(`/ordenes/ordenes/${orden.id}`)}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-full ${config.color}`}>
                                                        <config.icon className="h-4 w-4" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{orden.numero_orden}</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            {orden.cliente?.razon_social} - {orden.vehiculo?.patente || 'Sin vehículo'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Badge className={config.color}>{config.label}</Badge>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Button 
                                    variant="outline" 
                                    className="w-full mt-4"
                                    onClick={() => navigate('/ordenes/ordenes')}
                                >
                                    Ver todas las órdenes
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Accesos rápidos */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Accesos Rápidos</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/ordenes')}>
                            <ClipboardList className="h-5 w-5" />
                            <span className="text-xs">Órdenes</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/clientes')}>
                            <Users className="h-5 w-5" />
                            <span className="text-xs">Clientes</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/vehiculos')}>
                            <Car className="h-5 w-5" />
                            <span className="text-xs">Vehículos</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/inventario')}>
                            <Package className="h-5 w-5" />
                            <span className="text-xs">Stock</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/dispositivos-instalados')}>
                            <Cpu className="h-5 w-5" />
                            <span className="text-xs">Instalados</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/calendario')}>
                            <Calendar className="h-5 w-5" />
                            <span className="text-xs">Calendario</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/reportes')}>
                            <BarChart3 className="h-5 w-5" />
                            <span className="text-xs">Reportes</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/certificados')}>
                            <Award className="h-5 w-5" />
                            <span className="text-xs">Certificados</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2 border-dashed" onClick={() => navigate('/ordenes/importacion')}>
                            <FileText className="h-5 w-5" />
                            <span className="text-xs">Importar</span>
                        </Button>
                        <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/ordenes/configuracion')}>
                            <Settings className="h-5 w-5" />
                            <span className="text-xs">Config</span>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
